import React, { useState, useCallback, useImperativeHandle, forwardRef, useMemo, useEffect, useRef } from 'react';
import { motion, AnimatePresence, PanInfo } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface ProductImage {
  s3Key: string;
  url: string;
  order: number;
  filename?: string;
}

interface ImageCarouselProps {
  images: ProductImage[];
  productName?: string;
  isMobile?: boolean;
  onFirstImageLoad?: (img: HTMLImageElement) => void;
  /** The "1 / 2" counter; the product page hides it under its Verified tag (#467). */
  showCount?: boolean;
}

export interface ImageCarouselRef {
  goToPrevious: () => void;
  goToNext: () => void;
}

const SWIPE_THRESHOLD = 50;

const ImageCarousel = forwardRef<ImageCarouselRef, ImageCarouselProps>(
  ({ images, productName = 'Product', isMobile = false, onFirstImageLoad, showCount = true }, ref) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isHovering, setIsHovering] = useState(false);
    const firstImageCallbackFired = useRef(false);

    const sortedImages = useMemo(() => {
      return [...images].sort((a, b) => a.order - b.order);
    }, [images]);
    const hasMultipleImages = sortedImages.length > 1;

    const handlePrevious = useCallback(() => {
      setCurrentIndex((prev) => (prev === 0 ? sortedImages.length - 1 : prev - 1));
    }, [sortedImages.length]);

    const handleNext = useCallback(() => {
      setCurrentIndex((prev) => (prev === sortedImages.length - 1 ? 0 : prev + 1));
    }, [sortedImages.length]);

    const handleDotClick = useCallback((index: number) => {
      setCurrentIndex(index);
    }, []);

    // Swipe handler for mobile
    const handleDragEnd = useCallback((_: unknown, info: PanInfo) => {
      if (info.offset.x < -SWIPE_THRESHOLD) {
        handleNext();
      } else if (info.offset.x > SWIPE_THRESHOLD) {
        handlePrevious();
      }
    }, [handleNext, handlePrevious]);

    // Preload next image only (forward navigation is ~90% of usage; backward is fast via CDN cache)
    useEffect(() => {
      if (sortedImages.length <= 1) return;
      const nextIndex = (currentIndex + 1) % sortedImages.length;
      const url = sortedImages[nextIndex]?.url;
      if (!url) return;
      const img = new Image();
      img.src = url;
      return () => { img.src = ''; };
    }, [currentIndex, sortedImages]);

    // First image onLoad handler
    const handleImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
      if (currentIndex === 0 && !firstImageCallbackFired.current && onFirstImageLoad) {
        firstImageCallbackFired.current = true;
        onFirstImageLoad(e.currentTarget);
      }
    }, [currentIndex, onFirstImageLoad]);

    // Expose navigation methods to parent via ref
    useImperativeHandle(ref, () => ({
      goToPrevious: handlePrevious,
      goToNext: handleNext,
    }), [handlePrevious, handleNext]);

    if (sortedImages.length === 0) {
      return (
        <div className="relative w-full h-full bg-[var(--v-photo,rgb(255_255_255/0.06))] rounded-2xl flex items-center justify-center">
          <div className="text-[var(--v-text-3,rgb(255_255_255/0.5))] text-center text-sm">
            <p>No photos yet</p>
          </div>
        </div>
      );
    }

    const showArrows = isMobile || isHovering;

    return (
      <div
        className="relative w-full h-full rounded-2xl overflow-hidden bg-white"
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        tabIndex={0}
        role="region"
        aria-label={`Image carousel for ${productName}, showing image ${currentIndex + 1} of ${sortedImages.length}`}
      >
        {/* Main Image with optional swipe */}
        <AnimatePresence mode="wait">
          {isMobile && hasMultipleImages ? (
            <motion.div
              key={currentIndex}
              className="w-full h-full"
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.2}
              onDragEnd={handleDragEnd}
              initial={{ opacity: 0, scale: 1.05 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
            >
              <img
                src={sortedImages[currentIndex]?.url}
                alt={`${productName} - Image ${currentIndex + 1}`}
                className="w-full h-full object-contain pointer-events-none"
                decoding="async"
                {...(currentIndex === 0 ? { fetchPriority: 'high' as const } : {})}
                onLoad={handleImageLoad}
                crossOrigin="anonymous"
              />
            </motion.div>
          ) : (
            <motion.img
              key={currentIndex}
              src={sortedImages[currentIndex]?.url}
              alt={`${productName} - Image ${currentIndex + 1}`}
              className="w-full h-full object-contain"
              initial={{ opacity: 0, scale: 1.05 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              decoding="async"
              {...(currentIndex === 0 ? { fetchPriority: 'high' as const } : {})}
              onLoad={handleImageLoad}
              crossOrigin="anonymous"
            />
          )}
        </AnimatePresence>

        {/* Navigation Arrows */}
        {hasMultipleImages && (
          <>
            <motion.button
              onClick={handlePrevious}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 backdrop-blur-md shadow-lg flex items-center justify-center text-white hover:bg-black/70 transition-colors"
              initial={{ opacity: 0 }}
              animate={{ opacity: showArrows ? (isMobile ? 0.7 : 1) : 0 }}
              transition={{ duration: 0.2 }}
              aria-label="Previous image"
              tabIndex={0}
            >
              <ChevronLeft size={24} />
            </motion.button>

            <motion.button
              onClick={handleNext}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 backdrop-blur-md shadow-lg flex items-center justify-center text-white hover:bg-black/70 transition-colors"
              initial={{ opacity: 0 }}
              animate={{ opacity: showArrows ? (isMobile ? 0.7 : 1) : 0 }}
              transition={{ duration: 0.2 }}
              aria-label="Next image"
              tabIndex={0}
            >
              <ChevronRight size={24} />
            </motion.button>
          </>
        )}

        {/* Dots Indicator */}
        {hasMultipleImages && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 px-3 py-2 rounded-full bg-black/50 backdrop-blur-md shadow-lg">
            {sortedImages.map((_, index) => (
              <button
                key={index}
                onClick={() => handleDotClick(index)}
                className={`h-2 rounded-full transition-all duration-300 ${index === currentIndex
                    ? 'bg-white w-6'
                    : 'bg-white/50 hover:bg-white/80 w-2'
                  }`}
                aria-label={`Go to image ${index + 1}`}
                aria-current={index === currentIndex ? 'true' : 'false'}
              />
            ))}
          </div>
        )}

        {/* Image Counter */}
        {showCount && hasMultipleImages && (
          <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-black/50 backdrop-blur-md shadow-lg text-white text-sm font-medium">
            {currentIndex + 1} / {sortedImages.length}
          </div>
        )}
      </div>
    );
  }
);

ImageCarousel.displayName = 'ImageCarousel';

export default ImageCarousel;
